import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { UserCheck, X, Users, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { unwrap, apiError } from "@/lib/api";
import { EP } from "@/lib/endpoints";
import UserAvatar from "@/components/UserAvatar";
import SubSettingsShell from "@/components/SubSettingsShell";

/**
 * FollowRequests — 1:1 web port of iOS `settings/follow-requests.tsx`
 * (spec 16.4).  Two sub-tabs:
 *   • Received — pending incoming requests (accept / reject).
 *   • Sent     — outgoing pending requests (revoke).
 *
 * Both lists funnel every requester/target shape the backend can emit
 * through `normalizeUser` so avatar + username + display name always
 * resolve, regardless of whether the person is nested under `requester`,
 * `profile`, `user`, `from_user`, `sender`, `target`, `recipient`, etc.
 */

const BRAND = "hsl(var(--brand))";
const DANGER = "#FF3B30";

// -------------------------- normalization ----------------------------
function normalizeUser(r, keys) {
  let p = r || {};
  for (const k of keys) {
    if (r?.[k] && typeof r[k] === "object") {
      p = r[k];
      break;
    }
  }
  return {
    ...p,
    user_id:
      p.user_id ??
      p.id ??
      r?.requester_id ??
      r?.requester_user_id ??
      r?.target_user_id ??
      r?.target_id ??
      r?.user_id,
    username:
      p.username ?? r?.requester_username ?? r?.target_username ?? r?.username,
    display_name:
      p.display_name ??
      p.name ??
      r?.requester_display_name ??
      r?.target_display_name ??
      r?.display_name,
    is_verified: p.is_verified ?? p.verified ?? r?.is_verified,
    profile_photo_url:
      p.profile_photo_url ||
      p.profile_pic_image_url ||
      p.profile_photo ||
      p.profile_pic_url ||
      p.avatar_url ||
      p.photo_url ||
      r?.profile_photo_url ||
      r?.profile_pic_image_url ||
      r?.profile_photo ||
      r?.profile_pic_url ||
      r?.avatar_url ||
      "",
  };
}

const normalizeRequester = (r) =>
  normalizeUser(r, ["requester", "requester_profile", "profile", "user", "from_user", "sender"]);
const normalizeTarget = (r) =>
  normalizeUser(r, ["target", "target_user", "target_profile", "recipient", "to_user", "profile", "user"]);

const requestId = (r) => r?.id ?? r?.request_id ?? r?.follow_request_id;

function unwrapList(d) {
  return Array.isArray(d)
    ? d
    : d?.items ||
        d?.requests ||
        d?.follow_requests ||
        d?.sent ||
        d?.data?.items ||
        d?.data ||
        [];
}

// -------------------------- component --------------------------------
export default function FollowRequests() {
  const [tab, setTab] = useState("received"); // "received" | "sent"
  const [received, setReceived] = useState([]);
  const [sent, setSent] = useState([]);
  const [loadingReceived, setLoadingReceived] = useState(true);
  const [loadingSent, setLoadingSent] = useState(false);
  const [sentFetched, setSentFetched] = useState(false);
  const [err, setErr] = useState("");
  const [busyIds, setBusyIds] = useState(new Set());

  // Initial received-tab fetch.
  useEffect(() => {
    (async () => {
      try {
        const res = await api.get(EP.myFollowRequests(1, 50));
        setReceived(unwrapList(unwrap(res.data)));
      } catch (e) {
        setErr(apiError(e));
      } finally {
        setLoadingReceived(false);
      }
    })();
  }, []);

  // Lazy-fetch sent list the first time the user opens the tab.
  const ensureSentLoaded = useCallback(async () => {
    if (sentFetched) return;
    setLoadingSent(true);
    try {
      const res = await api.get(EP.myFollowRequestsSent(1, 50));
      setSent(unwrapList(unwrap(res.data)));
      setSentFetched(true);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoadingSent(false);
    }
  }, [sentFetched]);

  const markBusy = (rid, on) =>
    setBusyIds((s) => {
      const n = new Set(s);
      if (on) n.add(rid);
      else n.delete(rid);
      return n;
    });

  // Accept / reject an INCOMING request — both require { username } body.
  const decide = async (rid, requesterUsername, action) => {
    markBusy(rid, true);
    try {
      const ep =
        action === "accept"
          ? EP.acceptFollowRequest(rid)
          : EP.rejectFollowRequest(rid);
      await api.post(ep, { username: requesterUsername });
      setReceived((prev) => prev.filter((r) => requestId(r) !== rid));
      toast.success(
        action === "accept" ? "Follow request accepted." : "Follow request declined."
      );
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      markBusy(rid, false);
    }
  };

  // Revoke a SENT request — POST /me/follow_requests/revoke { user_id, username }
  // where user_id / username identify the TARGET of the request.
  const revoke = async (rid, target) => {
    markBusy(rid, true);
    try {
      await api.post(EP.revokeFollowRequest, {
        user_id: target.user_id,
        username: target.username,
      });
      setSent((prev) => prev.filter((r) => requestId(r) !== rid));
      toast.success("Follow request revoked.");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      markBusy(rid, false);
    }
  };

  const activeLoading = tab === "received" ? loadingReceived : loadingSent;
  const activeList = tab === "received" ? received : sent;
  const activeEmptyIcon = tab === "received" ? Users : Send;
  const activeEmptyText =
    tab === "received" ? "No pending follow requests." : "No sent follow requests.";
  const subtitle =
    tab === "received"
      ? "People who want to follow your private account."
      : "Requests you sent that are waiting for approval. Revoke to withdraw one.";

  return (
    <SubSettingsShell title="Follow requests" testId="follow-requests-screen">
      {/* Received / Sent segmented pill toggle */}
      <div className="flex gap-2 mb-3" data-testid="follow-requests-tabs">
        <TabBtn
          active={tab === "received"}
          onClick={() => setTab("received")}
          Icon={Users}
          label="Follow Requests Received"
          testId="follow-requests-tab-received"
        />
        <TabBtn
          active={tab === "sent"}
          onClick={() => {
            setTab("sent");
            ensureSentLoaded();
          }}
          Icon={Send}
          label="Follow Requests Sent"
          testId="follow-requests-tab-sent"
        />
      </div>

      {/* Sub-title */}
      <p className="text-muted-foreground text-[13.5px] leading-[19px] mb-3">
        {subtitle}
      </p>

      {activeLoading ? (
        <div className="flex justify-center py-10">
          <Loader2
            className="w-5 h-5 animate-spin"
            style={{ color: BRAND }}
            data-testid="follow-requests-loading"
          />
        </div>
      ) : err && tab === "received" ? (
        <div
          className="rounded-2xl p-4"
          style={{ backgroundColor: "rgba(255, 59, 48, 0.13)" }}
          data-testid="follow-requests-error"
        >
          <p className="text-[#FF3B30] text-sm">{err}</p>
        </div>
      ) : activeList.length === 0 ? (
        <EmptyList Icon={activeEmptyIcon} text={activeEmptyText} />
      ) : tab === "received" ? (
        <RowList>
          {received.map((r, i) => {
            const rid = requestId(r);
            const requester = normalizeRequester(r);
            const busy = busyIds.has(rid);
            const isLast = i === received.length - 1;
            return (
              <UserRow
                key={rid}
                person={requester}
                isLast={isLast}
                testId={`follow-request-${rid}`}
              >
                <button
                  type="button"
                  onClick={() => decide(rid, requester.username, "accept")}
                  disabled={busy}
                  className="h-9 rounded-full px-3.5 text-[13px] font-bold text-white flex items-center gap-1.5 hover:opacity-90 active:opacity-80 disabled:opacity-60 transition-opacity"
                  style={{ backgroundColor: BRAND }}
                  data-testid={`follow-request-accept-${rid}`}
                >
                  {busy ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <UserCheck size={14} strokeWidth={2.4} />
                  )}
                  Accept
                </button>
                <button
                  type="button"
                  onClick={() => decide(rid, requester.username, "reject")}
                  disabled={busy}
                  className="h-9 w-9 rounded-full border border-border flex items-center justify-center hover:bg-secondary disabled:opacity-60 transition-colors"
                  aria-label="Reject"
                  data-testid={`follow-request-reject-${rid}`}
                >
                  <X size={16} className="text-foreground" />
                </button>
              </UserRow>
            );
          })}
        </RowList>
      ) : (
        <RowList>
          {sent.map((r, i) => {
            const rid = requestId(r);
            const target = normalizeTarget(r);
            const busy = busyIds.has(rid);
            const isLast = i === sent.length - 1;
            return (
              <UserRow
                key={rid}
                person={target}
                isLast={isLast}
                testId={`follow-request-sent-${rid}`}
              >
                <button
                  type="button"
                  onClick={() => revoke(rid, target)}
                  disabled={busy}
                  className="h-9 rounded-full px-4 text-[13px] font-extrabold border-[1.25px] flex items-center justify-center gap-2 disabled:opacity-60 transition-colors"
                  style={{
                    borderColor: DANGER,
                    color: DANGER,
                    backgroundColor: "rgba(255,59,48,0.07)",
                  }}
                  data-testid={`follow-request-revoke-${rid}`}
                >
                  {busy ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    "Revoke"
                  )}
                </button>
              </UserRow>
            );
          })}
        </RowList>
      )}
    </SubSettingsShell>
  );
}

// -------------------------- sub-components ---------------------------
function TabBtn({ active, onClick, Icon, label, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className="flex-1 h-[38px] rounded-xl px-2 flex items-center justify-center gap-1.5 transition-all active:opacity-80"
      style={
        active
          ? { backgroundColor: BRAND, color: "#fff" }
          : {
              backgroundColor: "hsl(var(--secondary))",
              color: "hsl(var(--muted-foreground))",
            }
      }
    >
      <Icon size={13} strokeWidth={2} />
      <span
        className="text-[12.5px] font-bold truncate"
        style={{ color: active ? "#fff" : undefined }}
      >
        {label}
      </span>
    </button>
  );
}

function EmptyList({ Icon, text }) {
  return (
    <div
      className="rounded-[20px] border border-border bg-card py-12 flex flex-col items-center text-center"
      data-testid="follow-requests-empty"
    >
      <Icon size={22} className="text-muted-foreground mb-2.5" strokeWidth={1.7} />
      <p className="text-muted-foreground text-sm">{text}</p>
    </div>
  );
}

function RowList({ children }) {
  return (
    <div className="rounded-[20px] border border-border bg-card overflow-hidden">
      {children}
    </div>
  );
}

function UserRow({ person, isLast, testId, children }) {
  return (
    <div
      className={`flex items-center gap-3 px-4 py-3 ${
        isLast ? "" : "border-b border-border"
      }`}
      data-testid={testId}
    >
      <Link
        to={`/${person.username}`}
        className="flex items-center gap-3 flex-1 min-w-0 hover:opacity-80"
      >
        <UserAvatar user={person} size="md" />
        <div className="flex-1 min-w-0">
          <div className="text-foreground text-[14.5px] font-semibold truncate">
            {person.display_name || person.username}
          </div>
          <div className="text-muted-foreground text-[12.5px] truncate">
            @{person.username}
          </div>
        </div>
      </Link>
      <div className="flex items-center gap-2">{children}</div>
    </div>
  );
}
